import React, { useState, useMemo } from "react";
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Avatar,
  TextField,
  InputAdornment,
  Collapse,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
// Todos na variante Rounded: o resto do app já usa essa família, e misturar
// Filled com Rounded lado a lado fica visualmente desalinhado.
import AppsRoundedIcon from "@mui/icons-material/AppsRounded";
import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import { useNavigate, useLocation } from "react-router-dom";
import { useUsuario } from "../../Hooks/UsuarioHook";
import { app_base_url } from "../../Utils/Urls";
import {
  resolverSistemaDisplay,
  iniciaisSistema,
  hueSistema,
  hrefDoSistema,
} from "../../Utils/sistemaDisplay";
import {
  agruparPorCategoria,
  metadadosCategoria,
} from "../../Utils/categoriasSistema";
import { TrocarContaModal } from "../TrocarContaModal";
import { ConfirmarSaida } from "../ConfirmarSaida";
import LogoSinsoft from "../../Assets/logo-sinsoft.png";

export const SIDEBAR_WIDTH = 264;

/** Rótulo das seções da sidebar (NAVEGAÇÃO / MEUS SISTEMAS) — um só estilo. */
const ESTILO_ROTULO_SECAO = {
  px: 1.5,
  color: "text.disabled",
  fontWeight: 800,
  fontSize: "0.68rem",
  letterSpacing: 0.6,
  display: "block",
} as const;

interface SidebarProps {
  mobileOpen: boolean;
  onClose: () => void;
}

export function Sidebar({ mobileOpen, onClose }: SidebarProps) {
  const { usuario, sair } = useUsuario();
  const navigate = useNavigate();
  const location = useLocation();
  const [trocarAberto, setTrocarAberto] = useState(false);
  const [sairAberto, setSairAberto] = useState(false);
  const [buscaSistema, setBuscaSistema] = useState("");
  // Seções de "Meus sistemas" abertas. Começam fechadas; a escolha fica
  // lembrada no navegador (conveniência — se o storage falhar, só esquece).
  const [secoesAbertas, setSecoesAbertas] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem("@sidebar-secoes") ?? "[]");
    } catch {
      return [];
    }
  });
  const alternarSecao = (categoria: string) =>
    setSecoesAbertas((atual) => {
      const nova = atual.includes(categoria)
        ? atual.filter((c) => c !== categoria)
        : [...atual, categoria];
      try {
        localStorage.setItem("@sidebar-secoes", JSON.stringify(nova));
      } catch {}
      return nova;
    });

  // sistemas filtrados pela busca rápida (por título já resolvido)
  const termo = buscaSistema.trim().toLowerCase();
  const sistemasFiltrados = (usuario?.sistemas ?? []).filter((s) =>
    resolverSistemaDisplay(s, usuario.entidade?.banco)
      .titulo.toLowerCase()
      .includes(termo)
  );
  // Portal do Servidor já vem na lista? (pra não duplicar com o link fixo)
  const jaTemPortalServidor = (usuario?.sistemas ?? []).some((s) =>
    resolverSistemaDisplay(s, usuario.entidade?.banco)
      .titulo.toLowerCase()
      .includes("portal do servidor")
  );

  // Mesmo agrupamento da home (Utils/categoriasSistema.ts) — a sidebar e a home
  // precisam contar a mesma história sobre onde cada sistema mora.
  const gruposSistemas = useMemo(
    () => agruparPorCategoria(sistemasFiltrados),
    [sistemasFiltrados]
  );

  const irPara = (path: string) => {
    navigate(path);
    onClose();
  };

  const ativo = (fragmento: string) => location.pathname.includes(fragmento);

  // "Sistemas" usa o MESMO ícone do cabeçalho da home (AppsRounded) — antes era
  // um prédio (BusinessIcon), que não dizia nada sobre sistemas.
  const navItens = [
    { label: "Sistemas", icon: <AppsRoundedIcon />, path: `${app_base_url}/sistemas`, frag: "/sistemas" },
    { label: "Perfil", icon: <AccountCircleRoundedIcon />, path: `${app_base_url}/perfil`, frag: "/perfil" },
    ...(usuario?.usuario?.administrador
      ? [{ label: "Usuários", icon: <PeopleAltRoundedIcon />, path: `${app_base_url}/usuarios`, frag: "/usuarios" }]
      : []),
  ];

  const conteudo = (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
      }}
    >
      {/* Marca */}
      <Box
        sx={{
          height: 64,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          px: 2,
        }}
      >
        <Box
          sx={{
            bgcolor: "#fff",
            borderRadius: 2,
            px: 1,
            py: 0.75,
            display: "flex",
            border: "1px solid rgba(15,23,42,0.06)",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          }}
        >
          <Box
            component="img"
            src={LogoSinsoft}
            alt="Sinsoft Informática"
            sx={{ height: 26, width: "auto", display: "block" }}
          />
        </Box>
        <Typography sx={{ fontWeight: 800, lineHeight: 1.1 }}>Login Único</Typography>
      </Box>
      <Divider />

      {/* Navegação */}
      <List sx={{ px: 1.5, py: 0.75 }}>
        <Typography variant="caption" sx={ESTILO_ROTULO_SECAO}>
          NAVEGAÇÃO
        </Typography>
        {navItens.map((item) => (
          <ListItemButton
            key={item.label}
            selected={ativo(item.frag)}
            onClick={() => irPara(item.path)}
            sx={{
              borderRadius: 2,
              my: 0.2,
              py: 0.7,
              "& .MuiListItemIcon-root": { color: "text.secondary" },
              "&.Mui-selected": {
                bgcolor: "primary.main",
                color: "#fff",
                "& .MuiListItemIcon-root": { color: "#fff" },
                "&:hover": { bgcolor: "primary.dark" },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{ fontWeight: 600, fontSize: "0.9rem" }}
            />
          </ListItemButton>
        ))}
      </List>

      {/* Meus Sistemas */}
      {usuario?.sistemas?.length > 0 && (
        <>
          <Divider />

          {/* Coluna "Meus Sistemas": cabeçalho FIXO + lista rolável.
              minHeight:0 é obrigatório — sem ele um filho flex não encolhe
              abaixo do conteúdo e o overflow da lista nunca chega a acontecer
              (a sidebar inteira é que passaria a rolar). */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Fica PARADO enquanto a lista rola */}
            <Box sx={{ flexShrink: 0, px: 1.5, pt: 1, pb: 0.5 }}>
              <Typography variant="caption" sx={ESTILO_ROTULO_SECAO}>
                MEUS SISTEMAS
              </Typography>

              {/* Busca rápida (aparece quando há vários sistemas) */}
              {usuario.sistemas.length > 4 && (
                <TextField
                  value={buscaSistema}
                  onChange={(e) => setBuscaSistema(e.target.value)}
                  placeholder="Buscar sistema..."
                  size="small"
                  fullWidth
                  sx={{
                    mt: 1,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      fontSize: "0.85rem",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            </Box>

            <List
              sx={{
                px: 1.5,
                pt: 0,
                pb: 1,
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
              }}
            >
            {/* Sistemas agrupados por categoria, na mesma ordem da home. Cada
                seção é um grupo que abre/fecha (como o menu do Portal da
                Transparência). Só aparecem seções em que o usuário TEM sistema —
                agruparPorCategoria já omite as vazias. */}
            {gruposSistemas.map(({ categoria, sistemas }) => {
              const meta = metadadosCategoria(categoria);
              const IconeCategoria = meta.icone;
              // Abre sozinha buscando (o resultado não pode ficar escondido) ou
              // quando é a ÚNICA seção do usuário (fechada, só esconderia tudo).
              const aberta =
                termo !== "" ||
                gruposSistemas.length === 1 ||
                secoesAbertas.includes(categoria);

              return (
                <Box key={categoria}>
                  <ListItemButton
                    onClick={() => alternarSecao(categoria)}
                    aria-expanded={aberta}
                    sx={{ borderRadius: 2, my: 0.2, py: 0.75, px: 1.25 }}
                  >
                    <ListItemIcon sx={{ minWidth: 34 }}>
                      <Box
                        sx={{
                          display: "flex",
                          p: 0.5,
                          borderRadius: 1.5,
                          color: (t) =>
                            t.palette.mode === "dark"
                              ? `hsl(${meta.hue} 70% 72%)`
                              : `hsl(${meta.hue} 55% 38%)`,
                          bgcolor: (t) =>
                            t.palette.mode === "dark"
                              ? `hsl(${meta.hue} 40% 20%)`
                              : `hsl(${meta.hue} 72% 94%)`,
                        }}
                      >
                        <IconeCategoria sx={{ fontSize: 16 }} />
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={categoria}
                      primaryTypographyProps={{
                        fontWeight: 700,
                        fontSize: "0.8rem",
                        lineHeight: 1.25,
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        color: "text.disabled",
                        mx: 0.75,
                      }}
                    >
                      {sistemas.length}
                    </Typography>
                    <ExpandMoreRoundedIcon
                      sx={{
                        fontSize: 18,
                        color: "text.disabled",
                        transition: "transform .2s ease",
                        transform: aberta ? "rotate(180deg)" : "none",
                      }}
                    />
                  </ListItemButton>

                  <Collapse in={aberta} timeout="auto" unmountOnExit>
                  <Box sx={{ pl: 1 }}>
                  {sistemas.map((sistema) => {
                    const { titulo, logoSrc } = resolverSistemaDisplay(
                      sistema,
                      usuario.entidade?.banco
                    );
                    const hue = hueSistema(titulo);

                    return (
                      <ListItemButton
                        key={sistema.id}
                        component="a"
                        href={hrefDoSistema(sistema.url)}
                        sx={{
                          borderRadius: 2,
                          my: 0.2,
                          py: 0.6,
                          "&:hover": { bgcolor: "action.hover" },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {/* mesmo fallback da home: iniciais coloridas, nunca o
                              mesmo ícone genérico para todo sistema sem logo */}
                          <Avatar
                            variant="rounded"
                            src={logoSrc || undefined}
                            alt={titulo}
                            sx={{
                              width: 24,
                              height: 24,
                              // com logo: sem canto e sem recorte (o Avatar
                              // cortava as pontas do desenho) e sem bolinha
                              // pastel atrás — igual ao card da home
                              borderRadius: logoSrc ? 0 : 1.5,
                              overflow: logoSrc ? "visible" : "hidden",
                              fontSize: "0.62rem",
                              fontWeight: 800,
                              bgcolor: (t) =>
                                logoSrc
                                  ? "transparent"
                                  : t.palette.mode === "dark"
                                  ? `hsl(${hue} 42% 22%)`
                                  : `hsl(${hue} 72% 94%)`,
                              color: (t) =>
                                t.palette.mode === "dark"
                                  ? `hsl(${hue} 70% 78%)`
                                  : `hsl(${hue} 55% 32%)`,
                              "& img": { objectFit: "contain" },
                            }}
                          >
                            {iniciaisSistema(titulo)}
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={titulo}
                          primaryTypographyProps={{
                            fontSize: "0.85rem",
                            lineHeight: 1.3,
                          }}
                        />
                      </ListItemButton>
                    );
                  })}
                  </Box>
                  </Collapse>
                </Box>
              );
            })}

            {/* Link fixo do Portal do Servidor: só quando é servidor, ainda NÃO está
                na lista (evita duplicar) e casa com a busca */}
            {usuario?.usuario?.servidor &&
              !jaTemPortalServidor &&
              "portal do servidor".includes(termo) && (
                <ListItemButton
                  component="a"
                  href="https://solucoes.sinsoft.com.br/portal-do-servidor"
                  sx={{ borderRadius: 2, my: 0.3 }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <PeopleAltRoundedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Portal do Servidor" primaryTypographyProps={{ fontSize: "0.9rem" }} />
                </ListItemButton>
              )}

            {sistemasFiltrados.length === 0 &&
              !(usuario?.usuario?.servidor && !jaTemPortalServidor && "portal do servidor".includes(termo)) && (
                <Typography
                  variant="caption"
                  sx={{ px: 1.5, py: 1, display: "block", color: "text.disabled" }}
                >
                  Nenhum sistema encontrado.
                </Typography>
              )}
            </List>
          </Box>
        </>
      )}

      {/* Rodapé: acessar outra conta + sair.
          Hierarquia: "Acessar outra conta" é a ação comum e leva o preenchimento;
          "Sair" é rara e destrutiva, então fica discreta (sem fundo, texto menor)
          e só assume o vermelho no hover — antes as duas gritavam igual. */}
      <Box
        sx={{
          mt: "auto",
          px: 1.25,
          py: 1,
          borderTop: 1,
          borderColor: "divider",
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        <ListItemButton
          onClick={() => setTrocarAberto(true)}
          sx={{
            borderRadius: 2,
            py: 0.7,
            justifyContent: "center",
            bgcolor: "rgba(79,142,247,0.10)",
            color: "primary.main",
            "& .MuiListItemIcon-root": { color: "primary.main", minWidth: 0, mr: 0.75 },
            "&:hover": { bgcolor: "rgba(79,142,247,0.18)" },
          }}
        >
          <ListItemIcon>
            <SwapHorizRoundedIcon sx={{ fontSize: 18 }} />
          </ListItemIcon>
          <ListItemText
            primary="Acessar outra conta"
            sx={{ flexGrow: 0, my: 0 }}
            primaryTypographyProps={{ fontWeight: 700, fontSize: "0.82rem" }}
          />
        </ListItemButton>

        <ListItemButton
          onClick={() => setSairAberto(true)}
          sx={{
            borderRadius: 2,
            py: 0.5,
            justifyContent: "center",
            color: "text.secondary",
            "& .MuiListItemIcon-root": { color: "text.secondary", minWidth: 0, mr: 0.75 },
            "&:hover": {
              bgcolor: "rgba(211,47,47,0.08)",
              color: "error.main",
              "& .MuiListItemIcon-root": { color: "error.main" },
            },
          }}
        >
          <ListItemIcon>
            <LogoutRoundedIcon sx={{ fontSize: 17 }} />
          </ListItemIcon>
          <ListItemText
            primary="Sair"
            sx={{ flexGrow: 0, my: 0 }}
            primaryTypographyProps={{ fontWeight: 600, fontSize: "0.78rem" }}
          />
        </ListItemButton>
      </Box>

      <TrocarContaModal open={trocarAberto} onClose={() => setTrocarAberto(false)} />
      <ConfirmarSaida
        open={sairAberto}
        onClose={() => setSairAberto(false)}
        onConfirm={() => {
          setSairAberto(false);
          sair();
        }}
      />
    </Box>
  );

  return (
    <>
      {/* Desktop: fixa */}
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          position: "sticky",
          top: 0,
          height: "100vh",
          borderRight: 1,
          borderColor: "divider",
          boxShadow: (t) =>
            t.palette.mode === "dark"
              ? "2px 0 12px rgba(0,0,0,0.3)"
              : "2px 0 12px rgba(15,23,42,0.04)",
        }}
      >
        {conteudo}
      </Box>

      {/* Mobile: gaveta */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: "block", md: "none" } }}
      >
        {conteudo}
      </Drawer>
    </>
  );
}
